import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { countryConfig } from '../country';
import { formatBudget } from '../lib/format';
import { ApiError } from '../lib/api';
import { requestInvoiceUsd, type InvoiceRequestInput } from '../lib/invoice-client';
import { KG_INVOICE_COPY as C } from '../content/kg-invoice';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';

/**
 * Kyrgyzstan's «Оплата по счёту» — a REQUEST form for the yearly plans, priced in
 * US dollars (`payments.invoiceUsd`). A separate component from the Uzbek
 * `InvoiceFlow` on purpose (coordinator, 2026-11-04): that one issues a numbered
 * so'm invoice and a PDF; this one only sends the company's details to the owner
 * (`POST /billing/invoice-request`), who issues the USD invoice by hand. Rendered
 * only where `invoiceUsdOffered()` holds, so neither UZ nor KZ can reach it.
 */
export function invoiceUsdOffered(planKey: string): boolean {
  const { payments } = countryConfig;
  return (
    payments.checkout === 'stripe' &&
    payments.bankInvoice &&
    !!payments.invoiceUsd?.amountsUsd[planKey]
  );
}

type Field = Exclude<keyof InvoiceRequestInput, 'planKey' | 'vatPayer' | 'offerAccepted'>;

const TEXT_FIELDS: ReadonlyArray<{ key: Field; type?: string; inputMode?: 'numeric' | 'email' }> = [
  { key: 'companyName' },
  { key: 'tin', inputMode: 'numeric' },
  { key: 'legalAddress' },
  { key: 'signerName' },
  { key: 'signerPosition' },
  { key: 'bankName' },
  { key: 'bankBik', inputMode: 'numeric' },
  { key: 'bankAccount', inputMode: 'numeric' },
  { key: 'bankSwift' },
  { key: 'startDate', type: 'date' },
  { key: 'invoiceEmail', type: 'email', inputMode: 'email' },
  { key: 'contact' },
];

function valid(f: Omit<InvoiceRequestInput, 'planKey'>): boolean {
  return (
    !!f.companyName.trim() &&
    !!f.legalForm &&
    /^\d{14}$/.test(f.tin) &&
    !!f.legalAddress.trim() &&
    !!f.signerName.trim() &&
    !!f.signerPosition.trim() &&
    !!f.bankName.trim() &&
    /^\d{6,9}$/.test(f.bankBik) &&
    /^\d{10,34}$/.test(f.bankAccount) &&
    /^[A-Za-z]{6}[A-Za-z0-9]{2}(?:[A-Za-z0-9]{3})?$/.test(f.bankSwift) &&
    /^\d{4}-\d{2}-\d{2}$/.test(f.startDate) &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.invoiceEmail) &&
    !!f.contact.trim() &&
    f.offerAccepted
  );
}

export function KgInvoiceRequest({
  planKey,
  planName,
  amountLocal,
  open,
  onOpenChange,
}: {
  planKey: 'pro-yearly' | 'business-yearly';
  planName: string;
  /** The plan's som price, shown «справочно» beside the dollar amount. */
  amountLocal: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const usd = countryConfig.payments.invoiceUsd;
  const amountUsd = usd?.amountsUsd[planKey] ?? 0;
  const [form, setForm] = useState<Omit<InvoiceRequestInput, 'planKey'>>({
    companyName: '',
    legalForm: C.legalForms[0],
    tin: '',
    legalAddress: '',
    signerName: '',
    signerPosition: '',
    bankName: '',
    bankBik: '',
    bankAccount: '',
    bankSwift: '',
    vatPayer: false,
    startDate: '',
    invoiceEmail: '',
    contact: '',
    offerAccepted: false,
  });
  const [tried, setTried] = useState(false);
  const submit = useMutation({
    mutationFn: () => requestInvoiceUsd({ planKey, ...form, tin: form.tin.trim() }),
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const ok = valid(form);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto p-5 sm:p-6">
        <DialogTitle className="text-[16px] font-semibold text-ink">
          {C.title(planName)}
        </DialogTitle>
        <p className="mt-1 text-[20px] font-bold text-ink" data-testid="kg-invoice-amount">
          {C.amount(amountUsd)}
        </p>
        <p className="text-[12.5px] text-ink-muted">
          {C.amountRef(
            formatBudget(String(amountLocal)) ?? String(amountLocal),
            usd?.rateSource ?? '',
          )}
        </p>
        {submit.isSuccess ? (
          <div className="mt-4" data-testid="kg-invoice-done">
            <p className="text-[15px] font-semibold text-ink">{C.doneTitle}</p>
            <p className="mt-1 text-[13.5px] text-ink-soft">
              {C.done(submit.data.orderNumber, form.invoiceEmail)}
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-4 w-full rounded-card border border-edge py-2.5 text-[14px] font-medium text-ink"
            >
              {C.close}
            </button>
          </div>
        ) : (
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setTried(true);
              if (ok && !submit.isPending) submit.mutate();
            }}
          >
            <label className="flex flex-col gap-1 text-[13px] text-ink-soft">
              {C.fields.legalForm}
              <select
                value={form.legalForm}
                onChange={(e) => set('legalForm', e.target.value)}
                className="h-11 rounded-card border-[1.5px] border-charcoal bg-cream px-3 text-[16px] text-charcoal"
              >
                {C.legalForms.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>
            {TEXT_FIELDS.map(({ key, type, inputMode }) => (
              <label key={key} className="flex flex-col gap-1 text-[13px] text-ink-soft">
                {C.fields[key]}
                <Input
                  type={type ?? 'text'}
                  inputMode={inputMode}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  required
                />
              </label>
            ))}
            <fieldset className="flex flex-col gap-1 text-[13px] text-ink-soft">
              <legend>{C.fields.vatPayer}</legend>
              <div className="flex gap-4">
                {([true, false] as const).map((v) => (
                  <label key={String(v)} className="flex items-center gap-1.5 text-ink">
                    <input
                      type="radio"
                      name="kg-invoice-vat"
                      checked={form.vatPayer === v}
                      onChange={() => set('vatPayer', v)}
                    />
                    {v ? C.yes : C.no}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="flex items-start gap-2 text-[13px] text-ink">
              <input
                type="checkbox"
                checked={form.offerAccepted}
                onChange={(e) => set('offerAccepted', e.target.checked)}
                className="mt-0.5"
              />
              {C.fields.offer}
            </label>
            <ul className="list-disc space-y-1 pl-5 text-[12px] leading-snug text-ink-muted">
              {C.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
            {tried && !ok && (
              <p role="alert" className="text-[12.5px] text-status-closing-text">
                {C.invalid}
              </p>
            )}
            {submit.isError && (
              <p role="alert" className="text-[12.5px] text-status-closing-text">
                {submit.error instanceof ApiError && submit.error.message
                  ? submit.error.message
                  : C.error}
              </p>
            )}
            <button
              type="submit"
              disabled={submit.isPending}
              aria-busy={submit.isPending || undefined}
              className="mt-1 w-full rounded-card bg-brand py-3 text-[15px] font-semibold text-white disabled:opacity-70"
            >
              {submit.isPending ? C.submitting : C.submit}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
